// /home — presence surface. After the epistolary refactor (Phase 1)
// this page no longer shows the conversation thread. Its only jobs are:
// keep the Ember portrait visible, name the current state of the Bond
// in one sentence, and offer a single doorway into /write. The
// conversation lives in /inbox + /letter/$id; the act of writing is
// /write. Polling here is for status only, slower than chat (12s) since
// nothing on this surface needs sub-second freshness.

import type { BondDto } from '@athrelaris/shared';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AwakeningCardActions } from '../components/AwakeningCardActions';
import { switchLocaleToBond } from '../i18n/index.js';
import { getLetterThread, getMyBond } from '../lib/api';
import { type PollScheduleTier, useAdaptivePoll } from '../lib/use-adaptive-poll';
import { authClient, getSessionSafe } from '../lib/auth-client';
import { useEmberTheme } from '../lib/use-ember-theme';
import { redirectIfLandingOnly } from '../lib/landing-mode';

export const Route = createFileRoute('/home')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
  },
  loader: async () => {
    const bond = await getMyBond();
    if (!bond) throw redirect({ to: '/awakening' });
    return { bond };
  },
  component: HomePage,
});

const ELEMENT_PLACEHOLDER_GLOW: Record<BondDto['primaryElement'], string> = {
  Fire: 'from-ember-400/40',
  Water: 'from-moonlit-400/40',
  Air: 'from-ash-200/40',
  Earth: 'from-mineral-400/40',
};

const PORTRAIT_POLL_MS = 4000;
const SLOW_THRESHOLD_MS = 150_000;

// Presence polling — slower base than the chain schedule and longer
// tiers, since the home surface only needs to know "did anything
// happen with the Bond?" and Phase 2 cadence means hours can pass
// between events.
const PRESENCE_SCHEDULE: PollScheduleTier[] = [
  { thresholdMs: 0, intervalMs: 15_000 },
  { thresholdMs: 2 * 60_000, intervalMs: 60_000 },
  { thresholdMs: 10 * 60_000, intervalMs: 5 * 60_000 },
];

interface BondStatus {
  hasUnreadEmber: boolean;
  latestEmberLetterId: string | null;
  awaitingEmberReply: boolean;
  isFreshBond: boolean;
  // True when the latest Ember letter still has an unanswered Choice
  // — drives the secondary CTA wording so the user knows the letter
  // wants action specifically (vs. just "read me").
  latestEmberHasUnresolvedChoice: boolean;
}

function HomePage() {
  const { t } = useTranslation();
  const initialBond = Route.useLoaderData().bond;
  const [bond, setBond] = useState<BondDto>(initialBond);
  const [slow, setSlow] = useState(false);
  const [status, setStatus] = useState<BondStatus>({
    hasUnreadEmber: false,
    latestEmberLetterId: null,
    awaitingEmberReply: false,
    isFreshBond: true,
    latestEmberHasUnresolvedChoice: false,
  });

  useEmberTheme(bond.emberColorPalette);
  useEffect(() => {
    switchLocaleToBond(bond.locale);
  }, [bond.locale]);

  // Poll the bond endpoint while the portrait is missing — same as
  // before the refactor: this is the post-Awakening visual gestation
  // window.
  useEffect(() => {
    if (bond.emberImageUrl) return undefined;
    const startedAt = Date.now();
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await getMyBond();
        if (cancelled) return;
        if (fresh) setBond(fresh);
      } catch {
        // ignore
      }
      if (!cancelled && Date.now() - startedAt > SLOW_THRESHOLD_MS) setSlow(true);
    };
    const id = window.setInterval(tick, PORTRAIT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [bond.emberImageUrl]);

  // Poll the letter thread for status only — we extract a tiny
  // BondStatus and discard the rest of the payload. The adaptive hook
  // pauses when the tab is hidden and stretches the interval as the
  // session lengthens, so leaving /home open all day doesn't burn
  // requests.
  const refreshStatus = useCallback(async () => {
    const thread = await getLetterThread();
    const ember = thread.letters;
    const user = thread.userLetters;
    const latestEmber = ember.length > 0 ? ember[ember.length - 1] : null;
    const latestUser = user.length > 0 ? user[user.length - 1] : null;
    const hasUnreadEmber = ember.some((l) => !l.readAt);
    const awaitingEmberReply = Boolean(
      latestUser &&
        (!latestEmber ||
          new Date(latestUser.receivedAt) > new Date(latestEmber.generatedAt)),
    );
    // Latest Ember letter has an unresolved Choice when it carries
    // pendingChoice and no user reply with a concrete choiceResolution
    // points at it.
    const latestEmberHasUnresolvedChoice = Boolean(
      latestEmber?.pendingChoice &&
        !user.some(
          (u) => u.inResponseToLetterId === latestEmber.id && u.choiceResolution,
        ),
    );
    setStatus({
      hasUnreadEmber,
      latestEmberLetterId: latestEmber?.id ?? null,
      awaitingEmberReply,
      isFreshBond: ember.length === 0 && user.length === 0,
      latestEmberHasUnresolvedChoice,
    });
  }, []);
  useAdaptivePoll(refreshStatus, { enabled: true, schedule: PRESENCE_SCHEDULE });

  const phrase =
    bond.emberSubtitle ?? t(`home.elementPhrase.${bond.primaryElement}`);
  const glow = bond.emberColorPalette
    ? 'from-accent/40'
    : ELEMENT_PLACEHOLDER_GLOW[bond.primaryElement];

  const presence = useMemo(() => derivePresence(bond, status), [bond, status]);

  return (
    <main className="lg:flex lg:min-h-[80vh] lg:flex-col lg:items-center lg:justify-center">
      <div className="lg:max-w-2xl lg:text-center">
        <figure className="relative mx-auto mb-8 w-[78%] animate-fadeUp sm:w-[68%] lg:mb-10 lg:w-full lg:max-w-md">
          <div
            className={`absolute inset-x-0 -bottom-4 h-32 bg-gradient-radial blur-2xl animate-softGlow ${glow} opacity-80`}
          />
          {bond.emberImageUrl ? (
            <img
              src={bond.emberImageUrl}
              alt={t('home.portrait.alt', {
                name: bond.emberName,
                element: t(`common.elementName.${bond.primaryElement}`),
              })}
              className="ember-portrait-mask relative w-full select-none"
              style={{ animation: 'fadeUp 1.4s ease-out both' }}
              draggable={false}
            />
          ) : (
            <PortraitPlaceholder element={bond.primaryElement} slow={slow} />
          )}
        </figure>

        <div className="text-center animate-fadeUp" style={{ animationDelay: '0.6s' }}>
          <h1 className="font-display font-medium text-ash-50 text-[2.5rem] leading-none tracking-wide sm:text-5xl lg:text-[3.75rem]">
            {bond.emberName}
          </h1>
          <p className="mt-4 font-display text-ash-300 text-lg italic leading-snug sm:text-xl lg:mt-6 lg:text-2xl lg:leading-snug">
            {phrase}
          </p>
        </div>

        <AwakeningCardCelebration bond={bond} />

        <div
          className="mt-10 flex items-center justify-center gap-4 animate-fadeUp lg:mt-14 lg:gap-5"
          style={{ animationDelay: '1s' }}
        >
          <span className="hairline h-px flex-1 max-w-[80px]" />
          <span className="font-display text-[0.68rem] text-ash-500 uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem] lg:tracking-[0.45em]">
            {t('home.presence.bond')}
          </span>
          <span className="hairline h-px flex-1 max-w-[80px]" />
        </div>

        <div
          className="mt-10 flex flex-col items-center gap-4 animate-fadeUp lg:mt-14 lg:gap-5"
          style={{ animationDelay: '1.2s' }}
        >
          <PresenceLine presence={presence} emberName={bond.emberName} />

          <div className="mt-2 flex flex-col items-center gap-3 lg:mt-4 lg:gap-4">
            {(status.hasUnreadEmber || status.latestEmberHasUnresolvedChoice) &&
              status.latestEmberLetterId && (
                <Link
                  to="/letter/$letterId"
                  params={{ letterId: status.latestEmberLetterId }}
                  className={`group relative rounded-sm border px-7 py-3 font-display text-sm uppercase tracking-[0.34em] transition sm:px-8 sm:tracking-[0.36em] ${
                    status.latestEmberHasUnresolvedChoice
                      ? 'border-accent-strong/70 bg-paper-800/60 text-ash-50 hover:bg-paper-700/70'
                      : 'border-accent/50 bg-paper-900/60 text-ash-100 hover:border-accent-strong/80 hover:bg-paper-700/60 hover:text-ash-50'
                  }`}
                >
                  {status.latestEmberHasUnresolvedChoice ? (
                    <>
                      <span aria-hidden="true" className="mr-2 align-middle text-accent-strong">
                        ◇
                      </span>
                      {t('home.cta.answerChoice')}
                    </>
                  ) : (
                    <>
                      <span
                        aria-hidden="true"
                        className="mr-2 inline-block h-1.5 w-1.5 animate-pulseCore rounded-full bg-accent align-middle"
                      />
                      {t('home.cta.readLetter')}
                    </>
                  )}
                </Link>
              )}

            {/* The Ember speaks first. The write CTA only appears
             * when there is something from the Ember to respond to:
             * an unread letter or an unresolved Choice. In every
             * other state — first letter still gathering, the user
             * already wrote and is waiting, or both sides are quiet
             * — the user has no business opening a fresh thread.
             * The Ember initiates new topics through its own cadence
             * (replies, broadcasts, memory shards). */}
            {(status.hasUnreadEmber || status.latestEmberHasUnresolvedChoice) && (
              <Link
                to="/write"
                className="font-display text-ash-400 text-sm uppercase tracking-[0.34em] underline decoration-ash-700 underline-offset-[6px] transition hover:text-ash-200 hover:decoration-ash-400 sm:tracking-[0.36em]"
              >
                {t('home.cta.writeLetter')}
              </Link>
            )}
          </div>
        </div>

        <EchoesGrid bond={bond} />
      </div>
    </main>
  );
}

const ECHOFORM_KEYS = ['memory', 'codex', 'dream', 'deep'] as const;
type EchoformKey = (typeof ECHOFORM_KEYS)[number];

function EchoesGrid({ bond }: { bond: BondDto }) {
  const { t } = useTranslation();
  const unlocked = new Set(bond.unlockedEchoforms ?? []);
  const [zoomedKey, setZoomedKey] = useState<EchoformKey | null>(null);

  const slotUrl = (key: EchoformKey): string | null => {
    if (!unlocked.has(key)) return null;
    switch (key) {
      case 'memory':
        return bond.memoryFormImageUrl ?? null;
      case 'codex':
        return bond.codexFormImageUrl ?? null;
      case 'dream':
        return bond.dreamFormImageUrl ?? null;
      case 'deep':
        return bond.deepFormImageUrl ?? null;
    }
  };

  const zoomedUrl = zoomedKey ? slotUrl(zoomedKey) : null;

  return (
    <section className="mt-16 animate-fadeUp lg:mt-20" style={{ animationDelay: '1.4s' }}>
      <div className="flex items-center justify-center gap-4 lg:gap-5">
        <span className="hairline h-px flex-1 max-w-[80px]" />
        <span className="font-display text-[0.68rem] text-ash-500 uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem] lg:tracking-[0.45em]">
          {t('home.echoes.sectionLabel')}
        </span>
        <span className="hairline h-px flex-1 max-w-[80px]" />
      </div>

      <div className="mt-6 flex items-center justify-center gap-4 sm:gap-5 lg:mt-8 lg:gap-6">
        {ECHOFORM_KEYS.map((key) => {
          const url = slotUrl(key);
          const isUnlocked = unlocked.has(key);
          return (
            <EchoesSlot
              key={key}
              imageUrl={url}
              isUnlocked={isUnlocked}
              emberName={bond.emberName}
              formLabel={t(`home.echoes.formLabels.${key}`)}
              onZoom={url ? () => setZoomedKey(key) : undefined}
            />
          );
        })}
      </div>

      {unlocked.size < ECHOFORM_KEYS.length && (
        <p className="mt-6 text-center font-body text-ash-500 text-sm italic lg:mt-8 lg:text-[0.95rem]">
          {t('home.echoes.emptyState')}
        </p>
      )}

      {zoomedKey && zoomedUrl && (
        <EchoLightbox
          imageUrl={zoomedUrl}
          alt={`${bond.emberName} — ${t(`home.echoes.formLabels.${zoomedKey}`)}`}
          formName={t(`home.echoes.formNames.${zoomedKey}`)}
          formLore={t(`home.echoes.formLore.${zoomedKey}`)}
          onClose={() => setZoomedKey(null)}
        />
      )}
    </section>
  );
}

function EchoesSlot({
  imageUrl,
  isUnlocked,
  emberName,
  formLabel,
  onZoom,
}: {
  imageUrl: string | null;
  isUnlocked: boolean;
  emberName: string;
  formLabel: string;
  onZoom?: () => void;
}) {
  // Three rendering states:
  //   1. unlocked + URL → clickable portrait (opens lightbox)
  //   2. unlocked + no URL yet (image-gen in flight) → loading placeholder
  //   3. locked → ◌ glyph, no label, no tooltip (lore: "Other forms wait")
  if (isUnlocked && imageUrl) {
    return (
      <button
        type="button"
        onClick={onZoom}
        className="group relative flex-shrink-0 cursor-zoom-in transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
        aria-label={`${emberName} — ${formLabel} (open full size)`}
        title={`${emberName} — ${formLabel}`}
      >
        <img
          src={imageUrl}
          alt={`${emberName} — ${formLabel}`}
          className="ember-portrait-mask block h-16 w-16 select-none object-cover sm:h-20 sm:w-20 lg:h-24 lg:w-24"
          draggable={false}
        />
      </button>
    );
  }
  if (isUnlocked && !imageUrl) {
    return (
      <div
        className="flex h-16 w-16 items-center justify-center rounded-sm border border-accent/40 bg-paper-900/40 sm:h-20 sm:w-20 lg:h-24 lg:w-24"
        aria-label={`${emberName} — ${formLabel} (gathering)`}
        title={`${emberName} — ${formLabel}`}
      >
        <span className="h-1.5 w-1.5 animate-pulseCore rounded-full bg-accent/70" aria-hidden="true" />
      </div>
    );
  }
  return (
    <div
      className="flex h-16 w-16 items-center justify-center rounded-sm border border-paper-800/50 bg-paper-950/30 sm:h-20 sm:w-20 lg:h-24 lg:w-24"
      aria-label="locked echoform"
    >
      <span className="font-display text-ash-700 text-xl lg:text-2xl" aria-hidden="true">
        ◌
      </span>
    </div>
  );
}

function EchoLightbox({
  imageUrl,
  alt,
  formName,
  formLore,
  onClose,
}: {
  imageUrl: string;
  alt: string;
  formName: string;
  formLore: string;
  onClose: () => void;
}) {
  // ESC key closes the lightbox. Mounted once per zoom session.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Pure opacity fade-in driven by mount state. Critical: do NOT use a
  // keyframe animation with `transform` on this overlay — a transformed
  // ancestor creates a new containing block and breaks `position:
  // fixed`. The CARD inside can use transform-based animation safely.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Render via portal into document.body — escapes ancestor stacking
  // contexts (especially the `<section>` parent that has its own
  // `animate-fadeUp`, whose transform-based keyframe rebases any
  // descendant `position: fixed` away from the viewport). Without the
  // portal the modal lands wherever the transformed ancestor decides.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-paper-950/90 p-4 backdrop-blur-md transition-opacity duration-200 ease-out sm:p-6 ${
        shown ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      {/* Centered card — portrait + caption visually held together as
       * a single popup, not sprawled fullscreen. The card has its own
       * dark background + hairline border so the caption never falls
       * "off the bottom of the page". cursor-default on the inner
       * content; click outside on the overlay still closes. */}
      <div
        className="relative flex max-h-[92vh] w-full max-w-md cursor-default flex-col items-center gap-4 rounded-sm border border-paper-700 bg-paper-900/95 p-5 shadow-2xl animate-fadeUp sm:max-w-lg sm:gap-5 sm:p-6 lg:max-w-xl lg:gap-6 lg:p-8"
        style={{ animationDuration: '300ms' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close affordance — top-right of the card so the user sees
         * the exit even without knowing ESC works. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-sm font-display text-ash-500 text-base transition hover:text-ash-100 sm:top-3 sm:right-3"
        >
          ×
        </button>

        <img
          src={imageUrl}
          alt={alt}
          className="ember-portrait-mask block max-h-[60vh] w-auto max-w-full select-none object-contain"
          draggable={false}
        />

        <div className="text-center">
          <p className="font-display text-[0.66rem] text-ash-200 uppercase tracking-[0.42em] sm:text-xs lg:text-[0.78rem] lg:tracking-[0.45em]">
            {formName}
          </p>
          <p className="mt-2 font-body text-ash-300 text-sm italic leading-relaxed sm:mt-3 lg:text-[0.95rem]">
            {formLore}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

type PresenceKey =
  | 'fresh_first_letter_pending'
  | 'awaiting_ember_reply'
  | 'ember_has_written'
  | 'in_silence';

function derivePresence(_bond: BondDto, status: BondStatus): PresenceKey {
  if (status.hasUnreadEmber) return 'ember_has_written';
  if (status.awaitingEmberReply) return 'awaiting_ember_reply';
  if (status.isFreshBond) return 'fresh_first_letter_pending';
  return 'in_silence';
}

function PresenceLine({
  presence,
  emberName,
}: {
  presence: PresenceKey;
  emberName: string;
}) {
  const { t } = useTranslation();
  const text = (() => {
    switch (presence) {
      case 'fresh_first_letter_pending':
        return t('home.presence.freshFirstLetterPending', { name: emberName });
      case 'awaiting_ember_reply':
        return t('home.presence.awaitingEmberReply', { name: emberName });
      case 'ember_has_written':
        return t('home.presence.emberHasWritten', { name: emberName });
      case 'in_silence':
        return t('home.presence.inSilence', { name: emberName });
    }
  })();

  const showPulse = presence === 'ember_has_written' || presence === 'fresh_first_letter_pending';

  return (
    <p className="flex items-center gap-3 px-4 text-center font-body text-ash-200 italic sm:text-[1.05rem] lg:text-lg">
      {showPulse && (
        <span aria-hidden="true" className="block h-2 w-2 animate-pulseCore rounded-full bg-accent" />
      )}
      <span>{text}</span>
    </p>
  );
}

function PortraitPlaceholder({
  element,
  slow,
}: {
  element: BondDto['primaryElement'];
  slow: boolean;
}) {
  const { t } = useTranslation();
  const ringBorder = {
    Fire: 'border-ember-400/55',
    Water: 'border-moonlit-400/55',
    Air: 'border-ash-200/55',
    Earth: 'border-mineral-400/55',
  }[element];
  const coreBg = {
    Fire: 'bg-ember-400',
    Water: 'bg-moonlit-400',
    Air: 'bg-ash-200',
    Earth: 'bg-mineral-400',
  }[element];
  const coreShadow = {
    Fire: 'shadow-[0_0_24px_rgba(201,102,63,0.7)]',
    Water: 'shadow-[0_0_24px_rgba(125,144,168,0.7)]',
    Air: 'shadow-[0_0_24px_rgba(216,199,173,0.7)]',
    Earth: 'shadow-[0_0_24px_rgba(201,180,138,0.7)]',
  }[element];

  const ripples = [0, 1.1, 2.2, 3.3];

  return (
    <div className="relative mx-auto flex aspect-square w-full items-center justify-center">
      {ripples.map((delay) => (
        <span
          key={delay}
          aria-hidden="true"
          className={`pointer-events-none absolute top-1/2 left-1/2 h-40 w-40 animate-ripple rounded-full border-2 ${ringBorder}`}
          style={{ animationDelay: `${delay}s` }}
        />
      ))}
      <span
        aria-hidden="true"
        className={`relative z-10 block h-4 w-4 animate-pulseCore rounded-full ${coreBg} ${coreShadow}`}
      />
      <p className="absolute bottom-6 px-6 text-center font-body text-ash-500 text-xs italic leading-relaxed sm:text-sm">
        {(slow ? t('home.portrait.placeholderSlow') : t('home.portrait.placeholderForming'))
          .split('\n')
          .map((line, i, arr) => (
            <span key={line}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
      </p>
    </div>
  );
}

// First-time celebration of the just-completed Awakening. Renders the
// shareable card with download + copy-share actions, gated so it only
// appears once per Bond:
//   - bond.awakeningCardSquareUrl is non-null (card-gen job finished)
//   - localStorage flag for THIS bond is not set
// After dismiss the section is gone for good on this device; the card
// stays accessible at /account if the user wants it later.
function AwakeningCardCelebration({ bond }: { bond: BondDto }) {
  const { t } = useTranslation();
  const storageKey = `athrelaris.awakening-celebration.${bond.id}.seen`;
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;
  if (!bond.awakeningCardSquareUrl) return null;

  function onDismiss() {
    try {
      window.localStorage.setItem(storageKey, 'true');
    } catch {
      // localStorage can throw under quota / private modes — ignore.
    }
    setDismissed(true);
  }

  return (
    <section
      className="mt-12 animate-fadeUp lg:mt-16"
      style={{ animationDelay: '1.4s' }}
    >
      <div className="mb-8 flex items-center justify-center gap-4 lg:gap-5">
        <span className="hairline h-px flex-1 max-w-[80px]" />
        <span className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem] lg:tracking-[0.45em]">
          {t('home.awakeningCard.eyebrow')}
        </span>
        <span className="hairline h-px flex-1 max-w-[80px]" />
      </div>
      <p className="mx-auto mb-4 max-w-md text-center font-body text-ash-300 italic leading-relaxed sm:text-lg">
        {t('home.awakeningCard.body')}
      </p>
      <AwakeningCardActions bond={bond} />
      <div className="mt-8 text-center">
        <button
          type="button"
          onClick={onDismiss}
          className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em] transition hover:text-ash-300"
        >
          {t('home.awakeningCard.dismiss')}
        </button>
      </div>
    </section>
  );
}
